import { useDataProvider, useNotify } from "ra-core";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

import { Field, SimpleSelect } from "../SimpleSelect";
import {
  EXPENSE_CATEGORIES,
  IRPF_RATES,
  VAT_MODES,
  VAT_RATES,
  type AccSupplier,
  type VatMode,
} from "./accounting";

const COUNTRIES = [
  { value: "ES", label: "España" },
  { value: "EU", label: "Otro país de la UE" },
  { value: "other", label: "Fuera de la UE" },
];

/** A supplier and the defaults used when adding one of its invoices. */
export const SupplierDialog = ({
  open,
  onOpenChange,
  supplier,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplier?: AccSupplier | null;
  onSaved: () => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [name, setName] = useState("");
  const [taxId, setTaxId] = useState("");
  const [country, setCountry] = useState<AccSupplier["country"]>("ES");
  const [category, setCategory] = useState("other");
  const [vatMode, setVatMode] = useState<VatMode>("normal");
  const [vatRate, setVatRate] = useState(21);
  const [irpfRate, setIrpfRate] = useState(0);
  const [recurring, setRecurring] = useState(false);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(supplier?.name ?? "");
    setTaxId(supplier?.tax_id ?? "");
    setCountry(supplier?.country ?? "ES");
    setCategory(supplier?.default_category ?? "other");
    setVatMode(supplier?.default_vat_mode ?? "normal");
    setVatRate(Number(supplier?.default_vat_rate ?? 21));
    setIrpfRate(Number(supplier?.default_irpf_rate ?? 0));
    setRecurring(supplier?.recurring ?? false);
    setNotes(supplier?.notes ?? "");
  }, [open, supplier]);

  const save = async () => {
    if (!name.trim()) return notify("Falta el nombre", { type: "warning" });
    setSaving(true);
    const data = {
      name: name.trim(),
      tax_id: taxId.trim().toUpperCase() || null,
      country,
      default_category: category,
      default_vat_mode: vatMode,
      default_vat_rate: vatRate,
      default_irpf_rate: irpfRate,
      recurring,
      notes: notes.trim() || null,
    };
    try {
      if (supplier) {
        await dataProvider.update("gula_acc_suppliers", {
          id: supplier.id,
          data,
          previousData: supplier,
        });
      } else {
        await dataProvider.create("gula_acc_suppliers", { data });
      }
      notify("Proveedor guardado", { type: "success" });
      onSaved();
      onOpenChange(false);
    } catch (error) {
      console.error(error);
      notify(
        "No se ha podido guardar (¿ya existe un proveedor con ese nombre?)",
        {
          type: "error",
        },
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {supplier ? "Editar proveedor" : "Nuevo proveedor"}
          </DialogTitle>
          <DialogDescription>
            Al añadir una factura suya se rellenan solos la categoría y el IVA.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nombre">
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>
          <Field label="NIF / CIF / VAT">
            <Input
              value={taxId}
              onChange={(event) => setTaxId(event.target.value)}
            />
          </Field>
          <SimpleSelect
            label="País"
            value={country}
            options={COUNTRIES}
            onChange={(value) => setCountry(value as AccSupplier["country"])}
          />
          <SimpleSelect
            label="Categoría habitual"
            value={category}
            options={EXPENSE_CATEGORIES}
            onChange={setCategory}
          />
          <SimpleSelect
            label="IVA de sus facturas"
            value={vatMode}
            options={VAT_MODES}
            onChange={(value) => setVatMode(value as VatMode)}
          />
          <SimpleSelect
            label="% IVA"
            value={String(vatRate)}
            options={VAT_RATES.map((rate) => ({
              value: String(rate),
              label: `${rate} %`,
            }))}
            onChange={(rate) => setVatRate(Number(rate))}
          />
          <SimpleSelect
            label="Retención IRPF"
            value={String(irpfRate)}
            options={IRPF_RATES.map((rate) => ({
              value: String(rate),
              label: rate ? `${rate} %` : "Sin retención",
            }))}
            onChange={(rate) => setIrpfRate(Number(rate))}
          />
          <label className="flex items-center gap-2 self-end pb-2 text-sm">
            <Switch checked={recurring} onCheckedChange={setRecurring} />
            Factura todos los meses
          </label>
          <div className="sm:col-span-2">
            <Textarea
              placeholder="Notas (dónde se descarga la factura, contacto…)"
              rows={2}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" disabled={saving} onClick={save}>
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
